/**
 * TableExpand — shared primitives
 *
 * Single source of truth for all micro-components used across expanded row panels.
 *
 * NoDataPlate   → empty-state placeholder inside a section
 * SpinnerBlock  → centred primary-coloured spinner with message
 * CopyButton    → copy-to-clipboard button with transient checkmark
 * Th            → styled <th> for invoice/data tables
 */
import React, { useState, useCallback } from 'react';
import { FiCheck, FiCopy } from 'react-icons/fi';
import { AppButton } from '@/components/atoms/AppButton';
import Spinner from '@/components/atoms/Spinner';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_BG, APP_SEMANTIC, APP_TEXT } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';

// ─── Th — table header cell ───────────────────────────────────────────────────
export const Th = ({
  children,
  className = '',
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <th
    className={`py-2.5 text-[10px] font-bold uppercase tracking-widest ${APP_TEXT.muted} ${className}`}
    style={style}
  >
    {children}
  </th>
);

// ─── Td — table data cell ─────────────────────────────────────────────────────
export const Td = ({
  children,
  className = '',
  align = 'left',
  colSpan,
  style,
}: {
  children?: React.ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
  colSpan?: number;
  style?: React.CSSProperties;
}) => (
  <td
    colSpan={colSpan}
    className={`px-3 py-3 align-middle ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''} ${className}`}
    style={style}
  >
    {children}
  </td>
);

// ─── Design tokens ────────────────────────────────────────────────────────────
export const GRADIENT =
  'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)';
export const RAINBOW =
  'linear-gradient(90deg, var(--color-primary) 0%, var(--color-secondary) 50%, var(--color-tertiary) 100%)';

// ─── StatCell — re-exported from canonical Cards/StatCell ─────────────────────

// ─── NoDataPlate ──────────────────────────────────────────────────────────────
export const NoDataPlate = ({
  message,
  icon,
}: {
  message: string;
  icon?: React.ReactNode;
}) => {
  return (
    <div
      className={`flex flex-col items-center justify-center py-10 px-6 gap-3 rounded-xl border border-dashed border-gray-200 dark:border-gray-700/60 ${APP_BG.canvas}`}
    >
      {icon ? (
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full ${APP_BG.canvas} text-gray-400 dark:text-gray-500`}
        >
          {icon}
        </div>
      ) : (
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full ${APP_BG.canvas}`}
        >
          <svg
            className="w-5 h-5 text-gray-400 dark:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-.375c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v.375c0 .621.504 1.125 1.125 1.125z"
            />
          </svg>
        </div>
      )}
      <Text
        as="p"
        size="base"
        weight="medium"
        color="muted"
        align="center"
        className="max-w-[200px] leading-snug"
      >
        {message}
      </Text>
    </div>
  );
};

// ─── SpinnerBlock ─────────────────────────────────────────────────────────────
export const SpinnerBlock = ({
  message = 'Loading…',
  testId,
}: {
  message?: string;
  testId?: string;
}) => {
  return (
    <div
      data-testid={testId ?? ''}
      className="flex flex-col items-center justify-center py-10 gap-3"
    >
      <Spinner size="lg" color="primary" />
      <Text
        as="p"
        size="base"
        weight="medium"
        color="muted"
        className="animate-pulse"
      >
        {message}
      </Text>
    </div>
  );
};

// ─── CopyButton ───────────────────────────────────────────────────────────────
export const CopyButton = ({
  value,
  size = 'sm',
  testId,
}: {
  value: string;
  size?: 'xs' | 'sm';
  testId?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [value]);

  return (
    <AppButton
      variant={copied ? 'success' : 'neutral'}
      size={size === 'xs' ? 'xs' : 'sm'}
      shape="default"
      icon={copied ? <FiCheck /> : <FiCopy />}
      onClick={handleCopy}
      ripple={false}
      data-testid={testId ?? tid('copy-button')}
    >
      {copied
        ? ORGANISM_PROPS.COPY_BUTTON.COPIED
        : ORGANISM_PROPS.COPY_BUTTON.COPY}
    </AppButton>
  );
};
